import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchInjuryReport } from '../utils/injuryReportApi';
import { applyInjuryReportClientFilters } from '../utils/injuryReportClientFilters';

const DEFAULT_FILTERS = {
	scope: 'all',
	position: '',
	team: '',
	status_bucket: '',
	search: '',
	sort: 'impact'
};

export default function useInjuryReport({ user, endpointTemplate, csrfToken, initialScope = 'all' }) {
	const [filters, setFilters] = useState({
		...DEFAULT_FILTERS,
		scope: initialScope
	});
	const [meta, setMeta] = useState(null);
	const [sourcePlayers, setSourcePlayers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const loadForScope = useCallback(
		async (scope) => {
			if (!user?.id || !endpointTemplate) return;

			setLoading(true);
			setError(null);

			try {
				const data = await fetchInjuryReport({
					endpointTemplate,
					userId: user.id,
					csrfToken,
					params: { scope, sort: 'impact' }
				});

				setMeta(data.meta || null);
				setSourcePlayers(data.players || []);
			} catch (err) {
				setError(err.message || 'Failed to load injury report');
				setMeta(null);
				setSourcePlayers([]);
			} finally {
				setLoading(false);
			}
		},
		[user?.id, endpointTemplate, csrfToken]
	);

	useEffect(() => {
		loadForScope(filters.scope);
	}, [loadForScope, filters.scope]);

	const players = useMemo(
		() => applyInjuryReportClientFilters(sourcePlayers, filters),
		[sourcePlayers, filters]
	);

	const updateFilter = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	};

	const setScope = (scope) => updateFilter('scope', scope);

	const reload = () => loadForScope(filters.scope);

	return {
		meta,
		players,
		loading,
		error,
		filters,
		updateFilter,
		setScope,
		reload
	};
}
